import React, { useMemo, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { makeStyles, IconButton } from '@material-ui/core'
import FindInPageOutlinedIcon from '@material-ui/icons/FindInPageOutlined'
import clsx from 'clsx'
import { useSearchClients } from '../../../../api/clients'
import { useAppContext } from '../../../../redux/slices/appContext'
import FastAvatar from '../../../../components/molecules/FastAvatar'
import DenaliLogo from '../common/DenaliLogo'
import ThreadAuditModal from './ThreadAuditModal'

const useStyles = makeStyles(() => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    minHeight: '32px',
    backgroundColor: 'var(--dai-color-surface-subtle, var(--surface-bg-color))',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--dai-color-text-dim, var(--text-color-secondary))',
    transition: 'box-shadow 200ms ease, border-bottom 200ms ease',
    zIndex: 10,
    position: 'relative',
    flexShrink: 0
  },
  headerScrolled: {
    borderBottom: '1px solid var(--color-neutral-300, #d1d5db)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
  },
  threadTitle: {
    fontWeight: 500,
    color: 'var(--dai-color-text, var(--text-color-main))',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  separator: {
    color: 'var(--dai-color-text-dim, var(--text-color-secondary))',
    margin: '0 6px'
  },
  clientName: {
    fontWeight: 600,
    color: 'var(--color-primary-600)'
  },
  logoWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    flexShrink: 0
  },
  auditButton: {
    padding: 4,
    '& svg': {
      fontSize: '16px',
      color: 'var(--color-neutral-400)'
    },
    '&:hover svg': {
      color: 'var(--color-neutral-600)'
    }
  }
}))

function ThreadFocusHeader ({ focus, threadName, threadId, isScrolled }) {
  const classes = useStyles()
  const { isSummitUser } = useAppContext()
  const [auditOpen, setAuditOpen] = useState(false)

  const handleOpenAudit = useCallback(() => setAuditOpen(true), [])
  const handleCloseAudit = useCallback(() => setAuditOpen(false), [])

  const isClientFocus = focus?.type === 'client' && focus?.subjectIdentifier

  const clientQuery = useMemo(() => ({
    filters: { clientId: [focus?.subjectIdentifier] },
    includes: { avatars: true },
    take: 1
  }), [focus?.subjectIdentifier])

  const { data: clientData, isLoading: isLoadingClient } = useSearchClients(clientQuery, {
    enabled: !!isClientFocus
  })

  const client = useMemo(() => {
    return (clientData?.data || []).at(0)
  }, [clientData])

  if (!threadName && !isClientFocus) {
    return null
  }

  return (
    <>
      <div className={clsx(classes.header, isScrolled && classes.headerScrolled)}>
        {isClientFocus ? (
          <>
            <FastAvatar
              avatarUrl={client?.avatarUrl}
              abbreviation={client?.clientAbbreviation}
              size='sm'
              isLoading={isLoadingClient}
            />
            <span className={classes.clientName}>{focus.subjectLabel}</span>
            {threadName && <span className={classes.separator}>-</span>}
          </>
        ) : (
          <div className={classes.logoWrapper}>
            <DenaliLogo size='28px' />
          </div>
        )}
        {threadName && (
          <span className={clsx(classes.threadTitle, 'font-serif')}>{threadName}</span>
        )}
        {isSummitUser && threadId && (
          <IconButton
            size='small'
            className={classes.auditButton}
            onClick={handleOpenAudit}
            title='Thread audit'
          >
            <FindInPageOutlinedIcon />
          </IconButton>
        )}
      </div>
      {isSummitUser && threadId && (
        <ThreadAuditModal
          threadId={threadId}
          open={auditOpen}
          onClose={handleCloseAudit}
        />
      )}
    </>
  )
}

ThreadFocusHeader.propTypes = {
  focus: PropTypes.shape({
    type: PropTypes.string,
    subjectIdentifier: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    subjectLabel: PropTypes.string
  }),
  threadName: PropTypes.string,
  threadId: PropTypes.string,
  isScrolled: PropTypes.bool
}

ThreadFocusHeader.defaultProps = {
  focus: null,
  threadName: null,
  threadId: null,
  isScrolled: false
}

export default ThreadFocusHeader
